/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */
import { useEffect, useMemo } from "react";
import {
	IDndDraggableRegistryEntry,
	IDndDragMoveEventData,
	IDndDropAreaRegistryEntry,
	IDndRegistry,
	IDndRegistryEntries,
	TDndDraggableID,
	TDndDropAreaID
} from "./DndTypes";

function createRegistry(): IDndRegistry {
	const entries: IDndRegistryEntries = {
		draggables: {},
		droppables: {}
	};

	const emitDragMove = (data: IDndDragMoveEventData) => {
		const draggables = Object.values(entries.draggables);
		const droppables = Object.values(entries.droppables);

		const allEntries = [ ...draggables, ...droppables ];

		for (let i = 0; i < allEntries.length; i++) {
			allEntries[i].onDragMove(data);
		}
	};

	const emitDrop = () => {
		const draggables = Object.values(entries.draggables);
		const droppables = Object.values(entries.droppables);

		const allEntries = [ ...draggables, ...droppables ];

		for (let i = 0; i < allEntries.length; i++) {
			allEntries[i].onDragDrop();
		}
	};

	const getDraggablesOfDropArea = (droppableId: TDndDropAreaID): IDndDraggableRegistryEntry[] => {
		return Object.values(entries.draggables)
			.filter((draggable) => draggable.droppableId === droppableId)
			.sort((a, b) => a.index - b.index);
	};

	const findDraggableById = (id: TDndDraggableID): IDndDraggableRegistryEntry | null => {
		return entries.draggables[id] || null;
	};

	const draggableApi: IDndRegistry["draggable"] = {
		register: (entry) => {
			entries.draggables[entry.id] = entry;
		},
		unregister: (draggableId) => {
			const current = findDraggableById(draggableId);

			if (!current) {
				return;
			}

			delete entries.draggables[draggableId];
		},
		findById: findDraggableById,
		exists: (id) => !!findDraggableById(id)
	};

	const findDropAreaById = (id: TDndDropAreaID): IDndDropAreaRegistryEntry | null => {
		return entries.droppables[id] || null;
	};

	const droppableApi: IDndRegistry["droppable"] = {
		register: (entry) => {
			entries.droppables[entry.id] = entry;
		},
		unregister: (droppableId) => {
			const current = findDropAreaById(droppableId);

			if (!current) {
				return;
			}

			delete entries.droppables[droppableId];
		},
		findById: findDropAreaById,
		exists: (id) => !!findDropAreaById(id)
	};

	const clean: IDndRegistry["clean"] = () => {
		entries.draggables = {};
		entries.droppables = {};
	};

	return {
		emitDragMove,
		emitDrop,
		getDraggablesOfDropArea,
		draggable: draggableApi,
		droppable: droppableApi,
		clean
	};
}

export function useDndRegistry(): IDndRegistry {
	const registry = useMemo<IDndRegistry>(createRegistry, []);

	useEffect(() => {
		return () => {
			// clean up the registry to avoid any leaks
			// doing it after an animation frame so that other things unmounting
			// can continue to interact with the registry
			requestAnimationFrame(registry.clean);
		};
	}, [ registry ]);

	return registry;
}
